// T-190 (v1.8.64, Session 46, 09.10.2026): Link aus der Terminliste "Nächste Termine" (Seite /termine, zum Einbetten auf
// frankenfloss.de) in den Buchungskalender: /booking?datum=JJJJ-MM-TT&zeit=HH:MM. Der Kalender öffnet damit den Monat, markiert
// den Tag und öffnet den Termin wie ein Klick. Reine Funktionen, getestet in terminLink.test.ts.

export interface TerminZiel {
  /** Datum im Format JJJJ-MM-TT */
  datum: string;
  /** Uhrzeit HH:MM oder null (nur den Tag markieren) */
  zeit: string | null;
  /** Tourart (Code) aus einer gefilterten Liste oder null (Review v1.8.64: sonst öffnet der Kalender die erste Tourart) */
  tour: string | null;
}

const TOUR = /^[A-Za-z0-9ÄÖÜäöüß _-]{1,50}$/;

const DATUM = /^(\d{4})-(\d{2})-(\d{2})$/;
const ZEIT = /^(\d{1,2}):(\d{2})(:\d{2})?$/;

/** Uhrzeit auf HH:MM kürzen ("11:00:00" wird "11:00", "9:30" wird "09:30"); unbekanntes Format bleibt unverändert. */
export function zeitKurz(zeit: string): string {
  const m = ZEIT.exec((zeit ?? '').trim());
  if (!m) return zeit;
  return `${m[1].padStart(2, '0')}:${m[2]}`;
}

/** true, wenn das Datum ein echtes Kalenderdatum im Format JJJJ-MM-TT ist. */
export function istDatum(datum: string | null | undefined): datum is string {
  const m = DATUM.exec(datum ?? '');
  if (!m) return false;
  const j = Number(m[1]);
  const mo = Number(m[2]);
  const t = Number(m[3]);
  const d = new Date(j, mo - 1, t);
  return d.getFullYear() === j && d.getMonth() === mo - 1 && d.getDate() === t;
}

/** true, wenn die Uhrzeit gültig ist (00:00 bis 23:59, Sekunden erlaubt). */
export function istZeit(zeit: string | null | undefined): zeit is string {
  const m = ZEIT.exec((zeit ?? '').trim());
  if (!m) return false;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  return h >= 0 && h <= 23 && mi >= 0 && mi <= 59;
}

/** Link in den Buchungskalender (relativ, funktioniert auf frankenfloss.net und im eingebetteten Fenster mit target _top). */
export function terminLink(datum: string, zeit?: string | null, tour?: string | null): string {
  const p = new URLSearchParams();
  p.set('datum', datum);
  if (zeit && istZeit(zeit)) p.set('zeit', zeitKurz(zeit));
  const t = (tour ?? '').trim();
  if (t && TOUR.test(t)) p.set('tour', t);
  return `/booking?${p.toString()}`;
}

/**
 * Liest datum und zeit aus der Adresse des Kalenders. Ungültige Angaben und Tage vor heute ergeben null; eine ungültige
 * Uhrzeit wird verworfen (nur der Tag wird markiert). heute im Format JJJJ-MM-TT (Ortszeit des Browsers).
 */
export function parseTerminLink(search: string | null | undefined, heute: string): TerminZiel | null {
  let p: URLSearchParams;
  try {
    p = new URLSearchParams(search ?? '');
  } catch {
    return null;
  }
  const datum = (p.get('datum') ?? '').trim();
  if (!istDatum(datum)) return null;
  if (istDatum(heute) && datum < heute) return null;
  const zeitRoh = p.get('zeit');
  const zeit = zeitRoh && istZeit(zeitRoh) ? zeitKurz(zeitRoh) : null;
  const tourRoh = (p.get('tour') ?? '').trim();
  const tour = tourRoh && TOUR.test(tourRoh) ? tourRoh : null;
  return { datum, zeit, tour };
}
